import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { requestKeys } from "@/domains/requests/api/keys";
import type {
  CameraRequest,
  CreateRequestInput,
  RequestStatus,
} from "@/domains/requests/api/types";
import {
  appendRequestDecision,
  createRequest,
  linkRequestToCase,
  updateRequestStatus,
} from "@/domains/requests/mocks/requestsService";
import { caseKeys } from "@/domains/cases/api/keys";
import type { CaseFile } from "@/domains/cases/api/types";
import { createCaseFromRequest } from "@/domains/cases/mocks/casesService";

export function useCreateRequest(): UseMutationResult<
  CameraRequest,
  Error,
  CreateRequestInput
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createRequest,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: requestKeys.all });
    },
  });
}

type UpdateStatusVars = {
  id: string;
  status: RequestStatus;
};

export function useUpdateRequestStatus(): UseMutationResult<
  CameraRequest,
  Error,
  UpdateStatusVars
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: UpdateStatusVars) => updateRequestStatus(id, status),
    onSuccess: (req) => {
      client.invalidateQueries({ queryKey: requestKeys.all });
      client.invalidateQueries({ queryKey: requestKeys.detail(req.id) });
    },
  });
}

type LinkVars = { id: string; caseId: string };

export function useLinkRequestToCase(): UseMutationResult<CameraRequest, Error, LinkVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, caseId }: LinkVars) => linkRequestToCase(id, caseId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: requestKeys.all });
    },
  });
}

type ApproveVars = {
  request: CameraRequest;
  manager: { id: string; name: string };
  message?: string;
};

type ApproveResult = { request: CameraRequest; case: CaseFile };

export function useApproveRequest(): UseMutationResult<ApproveResult, Error, ApproveVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ request, manager, message }: ApproveVars) => {
      const caseFile = await createCaseFromRequest({
        request: {
          id: request.id,
          title: request.title,
          location: { address: request.location.address },
          period: {
            startDate: request.period.startDate,
            endDate: request.period.endDate,
          },
          motivation: { reason: request.motivation.reason },
        },
        manager,
      });
      const updated = await appendRequestDecision({
        id: request.id,
        decision: {
          byId: manager.id,
          byName: manager.name,
          kind: "approved",
          message,
        },
        status: "approved",
        caseId: caseFile.id,
      });
      return { request: updated, case: caseFile };
    },
    onSuccess: ({ request }) => {
      client.invalidateQueries({ queryKey: requestKeys.all });
      client.invalidateQueries({ queryKey: requestKeys.detail(request.id) });
      client.invalidateQueries({ queryKey: caseKeys.all });
    },
  });
}

type RejectVars = {
  id: string;
  manager: { id: string; name: string };
  reason: string;
};

export function useRejectRequest(): UseMutationResult<CameraRequest, Error, RejectVars> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, manager, reason }: RejectVars) =>
      appendRequestDecision({
        id,
        decision: {
          byId: manager.id,
          byName: manager.name,
          kind: "rejected",
          message: reason,
        },
        status: "rejected",
      }),
    onSuccess: (req) => {
      client.invalidateQueries({ queryKey: requestKeys.all });
      client.invalidateQueries({ queryKey: requestKeys.detail(req.id) });
    },
  });
}

type ClarifyVars = {
  id: string;
  manager: { id: string; name: string };
  question: string;
};

export function useRequestClarification(): UseMutationResult<
  CameraRequest,
  Error,
  ClarifyVars
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, manager, question }: ClarifyVars) =>
      appendRequestDecision({
        id,
        decision: {
          byId: manager.id,
          byName: manager.name,
          kind: "clarificationRequested",
          message: question,
        },
        status: "awaitingClarification",
      }),
    onSuccess: (req) => {
      client.invalidateQueries({ queryKey: requestKeys.all });
      client.invalidateQueries({ queryKey: requestKeys.detail(req.id) });
    },
  });
}
